/**
 * Stock Chart Viewer - Technical Indicators
 */

import { state } from '../state.js';
import { elements } from '../ui/dom.js';

// ============================================
// Calculation Functions
// ============================================

export function calculateSMA(candleData, period) {
    const smaData = [];
    for (let i = period - 1; i < candleData.length; i++) {
        let sum = 0;
        for (let j = 0; j < period; j++) {
            sum += candleData[i - j].close;
        }
        smaData.push({
            time: candleData[i].time,
            value: sum / period,
        });
    }
    return smaData;
}

export function calculateEMA(candleData, period) {
    if (candleData.length < period) return [];

    const emaData = [];
    const k = 2 / (period + 1);

    // Initial SMA
    let sum = 0;
    for (let j = 0; j < period; j++) {
        sum += candleData[j].close;
    }
    let ema = sum / period;

    // First EMA value (at index period - 1)
    emaData.push({
        time: candleData[period - 1].time,
        value: ema
    });

    // Calculate rest
    for (let i = period; i < candleData.length; i++) {
        const close = candleData[i].close;
        ema = close * k + ema * (1 - k);
        emaData.push({
            time: candleData[i].time,
            value: ema
        });
    }

    return emaData;
}

export function calculateBollingerBands(candleData, period = 20, multiplier = 2) {
    const upperData = [];
    const lowerData = [];

    for (let i = period - 1; i < candleData.length; i++) {
        // Calculate SMA
        let sum = 0;
        for (let j = 0; j < period; j++) {
            sum += candleData[i - j].close;
        }
        const sma = sum / period;

        // Calculate StdDev
        let sumSqDiff = 0;
        for (let j = 0; j < period; j++) {
            const diff = candleData[i - j].close - sma;
            sumSqDiff += diff * diff;
        }
        const stdDev = Math.sqrt(sumSqDiff / period);

        upperData.push({
            time: candleData[i].time,
            value: sma + (stdDev * multiplier)
        });
        lowerData.push({
            time: candleData[i].time,
            value: sma - (stdDev * multiplier)
        });
    }

    return { upper: upperData, lower: lowerData };
}

export function calculateRSI(candleData, period = 14) {
    const rsiData = [];

    let gains = 0;
    let losses = 0;

    // First average gain/loss
    for (let i = 1; i <= period; i++) {
        const change = candleData[i].close - candleData[i - 1].close;
        if (change >= 0) {
            gains += change;
        } else {
            losses -= change;
        }
    }

    let avgGain = gains / period;
    let avgLoss = losses / period;

    // RSI calc function
    const calc = (avgGain, avgLoss) => {
        if (avgLoss === 0) return 100;
        const rs = avgGain / avgLoss;
        return 100 - (100 / (1 + rs));
    };

    rsiData.push({
        time: candleData[period].time,
        value: calc(avgGain, avgLoss)
    });

    // Smoothed averages for the rest
    for (let i = period + 1; i < candleData.length; i++) {
        const change = candleData[i].close - candleData[i - 1].close;
        const gain = change >= 0 ? change : 0;
        const loss = change < 0 ? -change : 0;

        avgGain = ((avgGain * (period - 1)) + gain) / period;
        avgLoss = ((avgLoss * (period - 1)) + loss) / period;

        rsiData.push({
            time: candleData[i].time,
            value: calc(avgGain, avgLoss)
        });
    }

    return rsiData;
}

export function calculateMACD(candleData, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
    // We need EMA data, but EMA function returns array of {time, value}
    // We need to align them.
    const closePrices = candleData.map(c => ({ time: c.time, close: c.close }));

    // EMA helper that works on simple array
    const getEMAArray = (data, p) => {
        if (data.length < p) return new Array(data.length).fill(null);

        const k = 2 / (p + 1);
        const result = new Array(data.length).fill(null);

        let sum = 0;
        for (let i = 0; i < p; i++) sum += data[i].close;
        let ema = sum / p;
        result[p - 1] = ema;

        for (let i = p; i < data.length; i++) {
            ema = data[i].close * k + ema * (1 - k);
            result[i] = ema;
        }
        return result;
    };

    const fastEMA = getEMAArray(closePrices, fastPeriod);
    const slowEMA = getEMAArray(closePrices, slowPeriod);

    const macdLine = [];
    const validStartIndex = slowPeriod - 1;

    for (let i = validStartIndex; i < closePrices.length; i++) {
        macdLine.push({
            time: closePrices[i].time,
            value: fastEMA[i] - slowEMA[i]
        });
    }

    // Signal Line is EMA of MACD Line
    // macdLine is array of {time, value}
    // We can use generic EMA calculation on it if we treat 'value' like 'close'
    const macdForSignal = macdLine.map(m => ({ time: m.time, close: m.value }));
    const signalEMA = calculateEMA(macdForSignal, signalPeriod);

    // Histogram = MACD - Signal
    const histogram = [];
    const signalMap = new Map(signalEMA.map(s => [s.time, s.value]));

    macdLine.forEach(m => {
        if (signalMap.has(m.time)) {
            const signal = signalMap.get(m.time);
            histogram.push({
                time: m.time,
                value: m.value - signal,
                color: (m.value - signal) >= 0 ? '#3fb950' : '#f85149'
            });
        }
    });

    return {
        macd: macdLine,
        signal: signalEMA,
        histogram: histogram
    };
}

// ============================================

export function calculateHeikinAshi(candleData) {
    const haData = [];
    if (candleData.length === 0) return haData;

    // First candle: standard OHLC
    let prevOpen = candleData[0].open;
    let prevClose = candleData[0].close;

    haData.push({
        time: candleData[0].time,
        open: prevOpen,
        high: candleData[0].high,
        low: candleData[0].low,
        close: prevClose
    });

    for (let i = 1; i < candleData.length; i++) {
        const c = candleData[i];

        // HA Close = (Open + High + Low + Close) / 4
        const haClose = (c.open + c.high + c.low + c.close) / 4;

        // HA Open = (Prev HA Open + Prev HA Close) / 2
        const haOpen = (prevOpen + prevClose) / 2;

        // HA High = Max(High, HA Open, HA Close)
        const haHigh = Math.max(c.high, haOpen, haClose);

        // HA Low = Min(Low, HA Open, HA Close)
        const haLow = Math.min(c.low, haOpen, haClose);

        haData.push({
            time: c.time,
            open: haOpen,
            high: haHigh,
            low: haLow,
            close: haClose
        });

        prevOpen = haOpen;
        prevClose = haClose;
    }

    return haData;
}

export function calculateIchimoku(candleData) {
    // Default settings: 9, 26, 52, 26
    const conversionPeriod = 9;
    const basePeriod = 26;
    const spanPeriod = 52;
    const displacement = 26;

    const dataLength = candleData.length;
    const ichimokuData = {
        conversion: [],
        base: [],
        spanA: [],
        spanB: [],
        lagging: []
    };

    // Helper to find High/Low over period
    const getHighLow = (start, end) => {
        if (start < 0) start = 0;
        if (end > dataLength) end = dataLength;
        let highest = -Infinity;
        let lowest = Infinity;
        for (let i = start; i < end; i++) {
            if (candleData[i].high > highest) highest = candleData[i].high;
            if (candleData[i].low < lowest) lowest = candleData[i].low;
        }
        return { high: highest, low: lowest };
    };

    for (let i = 0; i < dataLength; i++) {
        const time = candleData[i].time;

        // Conversion Line (Tenkan-sen): (9-period High + 9-period Low) / 2
        if (i >= conversionPeriod - 1) {
            const hl = getHighLow(i - conversionPeriod + 1, i + 1);
            ichimokuData.conversion.push({ time, value: (hl.high + hl.low) / 2 });
        }

        // Base Line (Kijun-sen): (26-period High + 26-period Low) / 2
        if (i >= basePeriod - 1) {
            const hl = getHighLow(i - basePeriod + 1, i + 1);
            ichimokuData.base.push({ time, value: (hl.high + hl.low) / 2 });
        }

        // Leading Span A (Senkou Span A): (Conversion + Base) / 2, shifted forward 26 periods
        // We calculate it for current index, but key is time[i + displacement]
        // Since lightweight-charts needs future time, we might need to project time forward.
        // For simplicity, we just plot it at standard time for now, or handle displacement in rendering.
        // Actually, Ichimoku usually projects forward. 
        // Let's stick to simple "lines on chart" for now without complex future projection logic 
        // unless we want to handle generating future timestamps.
        // Standard approach: Plot normally, user interprets displacement visually, 
        // OR we shift the series. Lightweight-charts doesn't support 'shifting' a series easily without mapped times.
        // Let's implement Span A/B for the *current* candle based on past data (no displacement) 
        // OR try to map simple future indexing if possible. 
        // Given complexity, let's implement standard calculation without forward projection for this MVP step.
        // WAIT: Accurate Ichimoku needs displacement. 
        // Let's calculate the values.
        if (ichimokuData.conversion.length > 0 && ichimokuData.base.length > 0) {
            // We need values from *past* to project to *today*. 
            // Actuall cloud is projected *forward*.
            // Let's keep it simple: Calculate values as defined today.
            // Span A = (Conversion + Base) / 2
            // Span B = (52-period High + 52-period Low) / 2

            // For Span B
            if (i >= spanPeriod - 1) {
                const hl = getHighLow(i - spanPeriod + 1, i + 1);
                ichimokuData.spanB.push({ time, value: (hl.high + hl.low) / 2 });
            }

            // For Span A, we need match logic.
            // Let's assume user accepts unshifted or basic plotting for V1.
            if (i >= basePeriod - 1) { // Need Conversion & Base
                const lastConv = ichimokuData.conversion[ichimokuData.conversion.length - 1];
                const lastBase = ichimokuData.base[ichimokuData.base.length - 1];
                if (lastConv && lastBase && lastConv.time === time && lastBase.time === time) {
                    ichimokuData.spanA.push({ time, value: (lastConv.value + lastBase.value) / 2 });
                }
            }
        }

        // Lagging Span (Chikou Span): Close plotted 26 periods in past.
        // Again, shifting is hard. We'll skip Lagging Span visual shift or just plot Close.
        // Let's omit Lagging Span series for now as it's just the close line shifted.
    }

    return ichimokuData;
}

export function calculateStochastic(candleData, period = 14, smoothK = 3, smoothD = 3) {
    const stochData = { k: [], d: [] };
    const kRaw = [];

    // Calculate %K
    for (let i = 0; i < candleData.length; i++) {
        if (i >= period - 1) {
            // Find High/Low over period
            let high = -Infinity;
            let low = Infinity;
            for (let j = 0; j < period; j++) {
                if (candleData[i - j].high > high) high = candleData[i - j].high;
                if (candleData[i - j].low < low) low = candleData[i - j].low;
            }

            const currentClose = candleData[i].close;
            let kValue = 50; // Default if flat
            if (high !== low) {
                kValue = ((currentClose - low) / (high - low)) * 100;
            }

            kRaw.push(kValue);

            // Smooth %K
            if (kRaw.length >= smoothK) {
                let sum = 0;
                for (let s = 0; s < smoothK; s++) sum += kRaw[kRaw.length - 1 - s];
                const smoothKVal = sum / smoothK;
                stochData.k.push({ time: candleData[i].time, value: smoothKVal });
            }
        }
    }

    // Calculate %D (SMA of %K)
    for (let i = 0; i < stochData.k.length; i++) {
        if (i >= smoothD - 1) {
            let sum = 0;
            for (let s = 0; s < smoothD; s++) {
                sum += stochData.k[i - s].value;
            }
            const dVal = sum / smoothD;
            stochData.d.push({ time: stochData.k[i].time, value: dVal });
        }
    }

    return stochData;
}

// ============================================
// UI Update Functions
// ============================================

export function updateIndicators() {
    if (!state.stockData || !state.stockData.candleData) return;

    const candleData = state.stockData.candleData;

    // Update SMA 20
    if (state.sma20Series) {
        if (state.indicators.sma20) {
            state.sma20Series.setData(calculateSMA(candleData, 20));
        } else {
            state.sma20Series.setData([]);
        }
    }

    // Update SMA 50
    if (state.sma50Series) {
        if (state.indicators.sma50) {
            const data = calculateSMA(candleData, 50);
            state.sma50Series.setData(data);
        } else {
            state.sma50Series.setData([]);
        }
    }

    // Update Bollinger Bands
    if (state.bbUpperSeries && state.bbLowerSeries) {
        if (state.indicators.bb) {
            const bbData = calculateBollingerBands(candleData);
            state.bbUpperSeries.setData(bbData.upper);
            state.bbLowerSeries.setData(bbData.lower);
        } else {
            state.bbUpperSeries.setData([]);
            state.bbLowerSeries.setData([]);
        }
    }

    // Update RSI
    if (state.rsiSeries) {
        if (state.indicators.rsi) {
            state.rsiSeries.setData(calculateRSI(candleData));
        } else {
            state.rsiSeries.setData([]);
        }
    }

    // Update MACD
    if (state.macdSeries && state.macdSignalSeries && state.macdHistSeries) {
        if (state.indicators.macd) {
            const macdData = calculateMACD(candleData);
            state.macdSeries.setData(macdData.macd);
            state.macdSignalSeries.setData(macdData.signal);
            state.macdHistSeries.setData(macdData.histogram);
        } else {
            state.macdSeries.setData([]);
            state.macdSignalSeries.setData([]);
            state.macdHistSeries.setData([]);
        }
    }

    // Update Ichimoku
    if (state.indicators.ichimoku && state.ichimokuSeries) {
        const ichimokuData = calculateIchimoku(candleData);
        state.ichimokuSeries.conversion.setData(ichimokuData.conversion);
        state.ichimokuSeries.base.setData(ichimokuData.base);
        state.ichimokuSeries.spanA.setData(ichimokuData.spanA);
        state.ichimokuSeries.spanB.setData(ichimokuData.spanB);
    } else if (state.ichimokuSeries) {
        state.ichimokuSeries.conversion.setData([]);
        state.ichimokuSeries.base.setData([]);
        state.ichimokuSeries.spanA.setData([]);
        state.ichimokuSeries.spanB.setData([]);
    }

    // Update Stochastic
    if (state.stochSeries && state.stochSeries.k && state.stochSeries.d) {
        if (state.indicators.stoch) {
            const stochData = calculateStochastic(candleData);
            state.stochSeries.k.setData(stochData.k);
            state.stochSeries.d.setData(stochData.d);
        } else {
            state.stochSeries.k.setData([]);
            state.stochSeries.d.setData([]);
        }
    }
}

export function toggleIndicator(indicator) {
    state.indicators[indicator] = !state.indicators[indicator];

    // Toggle button active state
    const btnId = `${indicator}-btn`;
    const btn = document.getElementById(btnId);
    if (btn) {
        btn.classList.toggle('active', state.indicators[indicator]);
    }

    // Special handling for resizing layout if panes are toggled
    const isPaneIndicator = ['rsi', 'macd'].includes(indicator);
    if (isPaneIndicator) {
        // Trigger a layout resize or chart visibility toggle
        document.dispatchEvent(new CustomEvent('indicator-toggle', { detail: { indicator } }));
    }

    updateIndicators();
}
